//-- Copyright (c) 2021 TMT Insights Group, LLC
import { createTheme, lighten } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { darkBaseTheme, globalBaseTheme, lightBaseTheme, colors as globalColors } from '@tmt-insights/oms_fe_core/constants';
import logo from '../assets/logo.svg';

// https://mui.com/customization/theming/

export const colors = {
  light: deepmerge(globalColors.light, {
    primary: {
      main: '#4785ff',
      dark: '#2c69df',
      light: '#568aee'
    },
    secondary: {
      main: '#FF9E00',
      light: '#f3a019',
      dark: '#d28200'
    },
    tertiary: {
      main: '#863bf5',
      dark: '#5400cf',
      light: '#842fff'
    },
    success: {
      main: '#21B220',
      dark: '#4b9e5d',
      light: '#84AB93'
    },
    warning: {
      main: '#FF9E00'
    },
    error: {
      main: '#ea4848',
      dark: '#a11720'
    },
    background: {
      default: '#EFF2F5',
      paper: '#ffffff'
    }
  }),
  dark: deepmerge(globalColors.dark, {
    primary: {
      main: '#4785ff',
      dark: '#2c69df',
      light: '#568aee'
    },
    secondary: {
      main: '#FF9E00',
      light: '#f3a019',
      dark: '#d28200'
    },
    tertiary: {
      main: '#863bf5',
      dark: '#5400cf',
      light: '#842fff'
    },
    success: {
      main: '#21B220',
      dark: '#4b9e5d',
      light: '#84AB93'
    },
    warning: {
      main: '#FF9E00'
    },
    error: {
      main: '#ea4848',
      dark: '#a11720'
    },
    background: {
      default: '#111216',
      paper: '#1A1D25'
    }
  })
};

const globalTheme = deepmerge(globalBaseTheme, {
  shape: { borderRadius: 8 },
  customOverrides: {
    Header: {
      // borderBottom: '3px solid',
      // borderImage: 'linear-gradient(90deg, #FF9E00 0%, #ea4848 31.77%, #21429C 86.98%, #0071FF 100%) 1',
      background: 'linear-gradient(90deg, #000 50%, #111 100%)',
      '& h1 img': {
        height: 20,
      }
    },
    TitleBar: {},
    JobTitleBar: {},
    TaskDrawer: {},
    StatusCard: {
      // boxShadow: '0px 12px 26px rgba(16, 30, 115, 0.06)'
    },
    LoadingBar: {},
    TypeLabel: {
      root: {
        fontSize: '.8rem',
      },
      series: {
        backgroundColor: '#C669FF'
      },
      episode: {
        backgroundColor: '#B5007D'
      },
      feature: {
        backgroundColor: '#52F6FF',
        color: '#000'
      },
      trailer: {
        backgroundColor: '#6400F8'
      }
    }
  },
  components: {}
});

const light = {
  palette: {
    mode: 'light',
    ...colors.light
  },
  customOverrides: {
    ...globalTheme.customOverrides,
    TitleBar: {
      backgroundColor: '#f7f7f7'
    },
    TaskDrawer: {
      '& label': {
        color: colors.light.default.dark + ' !important',
        fontWeight: '700'
      }
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          '--primary-main': colors.light.primary.main,
          '--primary-light': colors.light.primary.light,
          '--primary-dark': colors.light.primary.dark,
          '--secondary-main': colors.light.secondary.main,
          '--secondary-light': colors.light.secondary.light,
          '--secondary-dark': colors.light.secondary.dark,
          '--tertiary-main': colors.light.tertiary.main,
          '--tertiary-light': colors.light.tertiary.light,
          '--tertiary-dark': colors.light.tertiary.dark,
          '--default-main': colors.light.default.main,
          '--default-light': colors.light.default.light,
          '--default-dark': colors.light.default.dark,
          '--warning-main': colors.light.warning.main,
          '--warning-light': colors.light.warning.light,
          '--warning-dark': colors.light.warning.dark,
          '--success-main': colors.light.success.main,
          '--success-light': colors.light.success.light,
          '--success-dark': colors.light.success.dark,
          '--teal-main': colors.light.teal.main,
          '--teal-light': colors.light.teal.light,
          '--teal-dark': colors.light.teal.dark
        }
      }
    }
  }
};

const dark = {
  palette: {
    mode: 'dark',
    ...colors.dark
  },
  customOverrides: {
    ...globalTheme.customOverrides,
    TitleBar: {
      backgroundColor: '#1a1d25'
    },
    ExpandRow: {
      borderWidth: 2
    },
    TaskDrawer: {
      '& label': {
        color: lighten(colors.light.primary.main, 0.4) + ' !important',
        fontWeight: '600'
      }
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          '--primary-main': colors.dark.primary.main,
          '--primary-light': colors.dark.primary.light,
          '--primary-dark': colors.dark.primary.dark,
          '--secondary-main': colors.dark.secondary.main,
          '--secondary-light': colors.dark.secondary.light,
          '--secondary-dark': colors.dark.secondary.dark,
          '--tertiary-main': colors.dark.tertiary.main,
          '--tertiary-light': colors.dark.tertiary.light,
          '--tertiary-dark': colors.dark.tertiary.dark,
          '--default-main': colors.dark.default.main,
          '--default-light': colors.dark.default.light,
          '--default-dark': colors.dark.default.dark,
          '--warning-main': colors.dark.warning.main,
          '--warning-light': colors.dark.warning.light,
          '--warning-dark': colors.dark.warning.dark,
          '--success-main': colors.dark.success.main,
          '--success-light': colors.dark.success.light,
          '--success-dark': colors.dark.success.dark,
          '--teal-main': colors.dark.teal.main,
          '--teal-light': colors.dark.teal.light,
          '--teal-dark': colors.dark.teal.dark
        }
      }
    }
  }
};

const lightMergedWithGlobal = deepmerge(light, globalTheme);
const darkMergedWithGlobal = deepmerge(dark, globalTheme);

const lightTheme = createTheme(deepmerge(lightBaseTheme, lightMergedWithGlobal));
const darkTheme = createTheme(deepmerge(darkBaseTheme, darkMergedWithGlobal));

export default { darkTheme, lightTheme, logo };
